<template>
	<DocumentShareHeader v-if="isDocumentShared" />
	<pdf-header v-else />
    
	<div class="flex h-[calc(100vh-60px)] w-full flex-row">

		<div
			id="onboarding-generator-points"
			data-step="2"
			class="z-20 flex h-full overflow-visible"
		>
			<div class="z-30 h-full flex-none">
				<sidebar @new-resource="handleNewResource" />
			</div>
			<transition
				enter-active-class="ease-in-out duration-500 transform"
				enter-from-class="left-[-620px] -translate-x-full"
				enter-to-class="left-0 translate-x-0"

				leave-active-class="ease-in-out duration-500 transform"
				leave-from-class="translate-x-0 left-0 "
				leave-to-class="-translate-x-full left-[-620px]"

				mode="out-in"
				@enter="adjustMainContentByScroll"

				@after-leave="adjustMainContentByScroll"
			>
				<div
					v-if="bulletOpened && !showResumeScorer"
					class="max-w-[618px] min-w-[618px] bg-white "
				>
					<!-- bullet generator -->
					<pdf-bullet-generator class="border-r border-[#E9EEF2]" />
				</div>
			</transition>

		</div>

		<!-- main content -->
		<div 
			:class="[ adjustByScroll ? 'right-0] fixed top-[60px]': 'h-full']"
			class="flex w-[calc(100%-70px)] grow flex-col"
		>
			<!-- top toolbar -->
			<div class="flex flex-col">
				<ActionsBar
					v-if="!isDocumentShared"
					:is-on-cover-letter-editor="coverLetter"
					:number-of-pages="nrOfPages"
					:current-page="currentPage"
					:templates="templates"
					@change-page="builderToolsStore.scrollToPage"
					@regenerate="handleRegenerateCoverLetter"
					@regenerate-cover-letter-prompt="regenerateCoverLetterPromptHandler"
					@change-template="changeTemplateHandler"
				/>
        
				<!-- ruler toolbar -->
				<div
					v-if="!isDocumentShared"
					class="won-tag-light-gray h-[15px] w-full overflow-auto"
				>
					<div class="h-full">
						<div
							v-if="loaded"
							id="pdf-ruler"
							data-step="6"
							:style="rulerStyles"
							class="relative mx-auto flex h-full items-center justify-start bg-white"
						>
							<pdf-ruler />
							<pdf-draggable-tick
								:side="'left'"
								:style="{ 'left': rulerPaddings.leftPadding + 'px' }"
								class="absolute cursor-ew-resize"
								@moved="v => builderStyle.changeMargin(v, 'left')"
							/>
							<pdf-draggable-tick
								:side="'right'"
								:style="{ 'left': (builderStyle.getBuilderWidth - rulerPaddings.rightPadding) + 'px' }"
								class="absolute cursor-ew-resize"
								@moved="v => builderStyle.changeMargin(v, 'right')"
							/>
						</div>
					</div>
				</div>
			</div>
      
			<!-- actual builder -->
			<div
				:class="[{'bg-[#656e83]': !coverLetter, 'bg-[#F1F5F7]': coverLetter}, 'relative h-full overflow-auto']"
				@scroll="hideContextMenu"
			>
				<div
					v-if="loaded"
					class="mx-auto mt-[64px] mb-[40px]"
				>
					<div
						id="pages-nr-exceeded"
						ref="builderContent"
						class="builder-content relative m-auto bg-white"
						data-step="3"
						:style="pageStyles"
						@click.right="rightClickHandler"
					>
						<div 
							v-for="(page, index) in nrOfPages"
							:key="index"
							:class="['absolute inset-x-0 border-dashed border-gray-300', `page-${page}`]"
							:style="{
								'border-bottom-width':page > 1 ?'.1px' : '0px',
								'margin-top': pageBreakTop(page) + 'px'}
							"
						>
							<span
								v-if="page>1"
								class="absolute pl-2 text-xs text-gray-300 select-none"
							>
								page {{ page }}
							</span>
						</div>
            
						<div
							v-if="loading && coverLetter"
							class="absolute inset-0 z-200 mt-[-300px] flex items-center justify-center"
						>
							<BigSpinner />
						</div>
                
						<slot />
					</div>
				</div>
				<!-- EN-1067: v-show, not v-if — ScorerCard's watchers are what refetch the score after a save. -->
				<ScorerCard
					v-if="!coverLetter && !bulletOpened && !isDocumentShared"
					v-show="!showResumeScorer"
				/>
			</div>
		</div>

		<SidebarScorer
			v-if="!coverLetter"
		/>
	</div>

	<!-- create new cover letter -->
	<div
		v-if="coverLetter && false"
		class="fixed bottom-6 left-32"
	>
		<div class="relative mb-2 translate-x-[-28%] rounded-lg bg-white px-2 py-0.5">
			<span class="font-[Inter] text-xs font-semibold text-won-green">Create New Cover Letter</span>
		</div>
		<router-link
			:to="{ name: 'cover-letters.edit' }"
			@click="handleNewResource"
		>
			<page-action class="bg-linear-to-b from-[#073B5D] via-[#2A8176] to-[#2A8176]">
				<svg
					width="16"
					height="16"
					viewBox="0 0 16 16"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M12.6666 6.00033L13.5 4.16699L15.3333 3.33366L13.5 2.50033L12.6666 0.666992L11.8333 2.50033L9.99996 3.33366L11.8333 4.16699L12.6666 6.00033ZM7.66663 6.33366L5.99996 2.66699L4.33329 6.33366L0.666626 8.00033L4.33329 9.66699L5.99996 13.3337L7.66663 9.66699L11.3333 8.00033L7.66663 6.33366ZM12.6666 10.0003L11.8333 11.8337L9.99996 12.667L11.8333 13.5003L12.6666 15.3337L13.5 13.5003L15.3333 12.667L13.5 11.8337L12.6666 10.0003Z"
						fill="white"
					/>
				</svg>
			</page-action>
		</router-link>
	</div>

	<!-- triggering first time enter this page -->
	<BuilderOnboarding v-if="loaded && !coverLetter && token == null" />
	<!--
		EN-806: shorter "what's new" re-introduction for existing users. Mounted alongside
		BuilderOnboarding (same gating conditions) rather than instead of it — each component
		independently checks its own users.options flag and only one of the two can ever be
		eligible for a given user at a time (see the comment in modals/resume-score-whats-new.vue).
	-->
	<ResumeScoreWhatsNew v-if="loaded && !coverLetter && token == null" />
	<CoverLetterOnboarding v-if="loaded && coverLetter && token == null" />

	<!-- trigger first time when clicking download -->
	<SurveyModal
		:visible="visibleSurvey"
		link="https://docs.google.com/forms/d/1cZyHdxqmgIa8qp5atYWcIjBHGHDlM5EJ6GbZyYgoK5g/"
		@survey-modal:close="handleCloseSurvey()" 
	/>

	<change-template-modal
		v-if="loaded && !coverLetter"
		:templates="templates"
		:visible="changeTemplateVisible"
		:current-template="new String(data?.template).toLowerCase()"
		@close="() => changeTemplateVisible=false"
		@change="handleChangeTemplate"
	/>

	<!-- trigger for cover-letters -->
	<SelectResume
		v-if="isOnCoverLetterPage"
		:visible="(!generatedPromptForCoverLetter && !isOnboarding) || openedCoverLetter"
		@networking:limit-error="handleLimitError"
		@delete-modal:close="handleCloseGeneratorPrompt"
	/>

	<div v-if="!coverLetter && !isDocumentShared">
		<ResumeScoreModal />
	</div>

	<Teleport
		v-if="userHaveAccessTo('resume-share')"
		to="body"
	>
		<RemoveUserAccessModal />
	</Teleport>
	<ResumeServiceNudge />
	<ShareResumeModal
		v-if="!coverLetter && userHaveAccessTo('resume-share')"
		:resume="data"
		:resume-title="data?.title || 'notitle'"
	/>
	<PDFContextMenu
		:show="showContextMenu"
		:hide-context-menu="hideContextMenu"
	/>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, shallowRef, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { getAll as getAllTemplates } from '../api/templates';
import { regenerateCoverLetter } from '../api/cover-letters';
import shareResumeApi from '../api/share-resume';
import { setAuthState } from '../composables/auth';
import { isTextEntryTarget } from '../composables/useKeyboardShortcut';

import { useFeatureUsageStore } from '../store/feature-usage.store';
import { useBuilderStyles } from '../store/builder-style.store';
import { useBuilder } from '../store/builder.store';
import { useBulletGenerator } from '../store/bullet-store';
import { useOnboardingStore } from '../store/onboarding.store';
import { useResumeScorerStore } from '../store/scorer.store';
import { useTiptapStore } from '../store/tiptap.store';
import { useBuilderToolsStore } from '../store/builder-tools.store';
import { useMousePosition } from '../store/mouse-position.store';
import { useNotifications } from '../store/notifications.store';

import PageAction from '../components/page-action';
import Sidebar from '../components/sidebar';
import PdfHeader from '../components/pdf-builder/pdf-header';
import DocumentShareHeader from '../components/pdf-builder/document-share-header.vue';
import PdfBulletGenerator from '../components/pdf-builder/pdf-bullet-generator';
import ActionsBar from '../components/pdf-builder/actions-bar.vue';
import PdfDraggableTick from '../components/pdf-builder/pdf-draggable-tick';
import PdfRuler from '../components/pdf-builder/pdf-ruler';
import ScorerCard from '../components/resume-score/scorer-card.vue';
import SidebarScorer from '../components/resume-score/sidebar-scorer.vue';
import PDFContextMenu from '../components/pdf/pdf-context-menu.vue';
import ShareResumeModal from '../modals/resumes/share-resume-modal.vue';
import RemoveUserAccessModal from '../modals/remove-user-access.vue';

import SurveyModal from '../modals/survey';
import SelectResume from '../modals/select-resume';
import ChangeTemplateModal from '../modals/change-template';
import BuilderOnboarding from '../modals/builder-onboarding';
import ResumeScoreWhatsNew from '../modals/resume-score-whats-new.vue';
import CoverLetterOnboarding from '../modals/cover-letter-onboarding';
import ResumeScoreModal from '../modals/resumes/resume-score.vue';
import ResumeServiceNudge from '../modals/services-nudges/service-nudge-modal.vue';
// TODO: REVERT - Remove this import when builder-entry nudge experiment is reverted
import { useServicesNudges } from '../store/services-nudges.store';

import BigSpinner from '../icons/big-spinner.vue';

const route = useRoute();
const router = useRouter();
const id = route.params.id;
const { userHaveAccessTo } = setAuthState();

const isOnCoverLetterPage = computed(() => {
	return route.meta?.coverLetter || false;
});

const { meta: { coverLetter = false }, query } = route;
const templates = ref([]);
const adjustByScroll = ref(false);
const generatedPromptForCoverLetter = shallowRef(true);
const changeTemplateVisible = shallowRef(false);
const changeTemplateCalledFromSidebar = shallowRef(false);
const visibleSurvey = shallowRef(false);
const loading = shallowRef(false);
const showContextMenu = shallowRef(false);

const builder = useBuilder();
const tiptapStore = useTiptapStore();
const builderStyle = useBuilderStyles();
const bulletGenerator = useBulletGenerator();
const featuresUsageStore = useFeatureUsageStore();
const scorerStore = useResumeScorerStore();
const onboardingStore = useOnboardingStore();
const builderToolsStore = useBuilderToolsStore();
const mousePositionStore = useMousePosition();

const { token = null } = query;

const { selectedText, editor } = storeToRefs(tiptapStore);
const { opened: bulletOpened } = storeToRefs(bulletGenerator);
const { loaded, data, isDocumentShared } = storeToRefs(builder);
const { isOnboarding, openedCoverLetter } = storeToRefs(onboardingStore);
const { showResumeScorer } = storeToRefs(scorerStore);
const { page: pageStyles, ruler: rulerStyles, getRullerPaddings: rulerPaddings } = storeToRefs(builderStyle);
const { nrOfPages, currentPage } = storeToRefs(builderToolsStore);
const { pageBreakTop } = builderToolsStore;

const builderContent = ref(null);
builderToolsStore.observeBuilderContent(builderContent);

const notificationsStore = useNotifications();

const adjustMainContentByScroll = () => {
	adjustByScroll.value = !adjustByScroll.value;
};

const setShowContextMenu = () => {
	showContextMenu.value = true;
};

const hideContextMenu = () => {
	showContextMenu.value = false;
};

const rightClickHandler = (event) => {
	if (isDocumentShared.value) {return;}

	event.preventDefault();
	mousePositionStore.mouseEventHandler(event);
	setShowContextMenu();
};

const changeTemplateHandler = () => {
	changeTemplateVisible.value = true;
	changeTemplateCalledFromSidebar.value = true;
};

const regenerateCoverLetterPromptHandler = () => {
	generatedPromptForCoverLetter.value = false;
};

const handleNewResource = () => {
	if (isDocumentShared.value) {return;}

	// allow to change templates only for resumes
	if (! coverLetter) {
		if (! templates.value.length) {
			builder.init(null, router.replace, route);
			return;
		} 
		changeTemplateVisible.value = true;
		changeTemplateCalledFromSidebar.value = false;
    
    
		return;
	}

	builder.init(null, router.replace, route);
};

const handleChangeTemplate = ({ template }) => {
	if (! changeTemplateCalledFromSidebar.value) {
		builder.init(null, router.replace, route);
		return;
	}

	builder.changeTemplate(template);
};

const handleLimitError = () => {
	featuresUsageStore.setShowLimitExceed('cover_letter');
};

const handleCloseGeneratorPrompt = (message) => {
	generatedPromptForCoverLetter.value = true;
	if (! message) {return;}
	const formatteHTML = `<p>${  message.replace(/\n([ \t]*\n)+/g, '</p><p></p><p>').replace(/\n/g, '<br />')  }</p>`;
	builder.changeMessage(formatteHTML);
};

// eslint-disable-next-line no-unused-vars -- pre-existing, currently unreferenced; not part of this change
const showSurveyOnFirstDownload = () => {
	if (isDocumentShared.value) {return;}

	if (!localStorage.getItem('showSurveyOnFirstDownload')) {
		visibleSurvey.value = true;
		localStorage.setItem('showSurveyOnFirstDownload', true);
	}
};

const handleCloseSurvey = () => {
	visibleSurvey.value = false;
};

const handleRegenerateCoverLetter = (allText = false) => {
	if (isDocumentShared.value) {return;}

	loading.value = true;
	let textToRegenerate;

	if (allText) {
		textToRegenerate = editor.value.getText();
	} else {
		textToRegenerate = selectedText.value;
	}

	regenerateCoverLetter(route.params.id, {
		// form data
		selected_text: textToRegenerate,
		previous_cover_letter: editor.value.getText(),
	})
		.catch((err) => {
			if (err?.response?.status === 429) {
				handleLimitError();
			}
		})
		.then((res) => {
			const completion = res?.data?.completion;

			// if completion is empty we not charge, and show error message.
			if (! completion?.replace(/\s/g, '').length ) {
				notificationsStore.notify('Please subscribe to access unlimited WonsultingAI usage!', 'error');
			} else {
				if (allText) {
					builder.updateMessage(editor.value, completion);
				} else {
					builder.updateSection(editor.value, completion );
				}
			}
		})
		.finally(() => {
			setTimeout(() => {
				loading.value = false;
			}, 500);
		});
};

// keys binding
onMounted(() => {
	if (isDocumentShared.value) {return;}

	document.onkeydown = function(e) {
		if (['+', '='].includes(e.key) && (e.ctrlKey || e.metaKey)) {
			e.preventDefault();
			builderStyle.zoomIn();
		}

		if (e.key === '-' && (e.ctrlKey || e.metaKey)) {
			e.preventDefault();
			builderStyle.zoomOut();
		}

		if (e.key === '0' && (e.ctrlKey || e.metaKey)) {
			e.preventDefault();
			builderStyle.resetZoom();
		}

		// EN-1137: inside a field, undo and cut belong to the field. TipTap's own history already
		// handles Cmd/Ctrl+Z there, so running the document undo as well swapped the whole resume
		// in underneath the focused editor mid-keystroke.
		if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !isTextEntryTarget(e.target)) {
			e.preventDefault();
			builder.undo();
		}

		if (e.key === 'x' && (e.ctrlKey || e.metaKey) && !isTextEntryTarget(e.target)) {
			e.preventDefault();
			builder.redo();
		}

		if (e.key === 'j' && (e.ctrlKey || e.metaKey)) {
			e.preventDefault();
			builderStyle.textMinus();
		}

		if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
			e.preventDefault();
			builderStyle.textPlus();
		}
	};
});

onMounted(() => {
	if (isDocumentShared.value) {return;}

	builderToolsStore.scrollEventListener();
});

onUnmounted(() => {
	if (isDocumentShared.value) {return;}

	document.removeEventListener('wheel', builderToolsStore.scrollEventListener);
});

// EN-996: a reload or navigation kills deferred and in-flight autosaves; send the
// document once more on a keepalive request the browser lets complete.
const flushPendingSave = () => {
	builder.flushSave();
};

// The layout is rendered from route meta, outside <router-view>, so onBeforeRouteLeave
// never fires here; a global guard, removed on unmount, holds the navigation until a
// debounced preview has been rendered from the still-mounted document.
let removePreviewGuard = null;

onMounted(() => {
	if (token == null) {
		window.addEventListener('pagehide', flushPendingSave);
		removePreviewGuard = router.beforeEach(() => builder.flushPreview());
	}
});

onUnmounted(() => {
	window.removeEventListener('pagehide', flushPendingSave);
	removePreviewGuard?.();
});

onMounted(async () => {
	if (token == null) {
		builder.init(id, router.replace, route);
	} else {
		try {
			const sharedResume = await shareResumeApi.getResumeWithToken(token);

			builder.initUsingObject(sharedResume, {
				isDocumentEditable: false,
				showPersonalInformation: true,
				isDocumentShared: true
			});
		} catch {
			router.push({ name: 'resume-not-found' });
		}
	}
});

onMounted(async () => {
	if (token == null) {
		templates.value = await getAllTemplates();
	}
});

watch(loaded, (newval) => {
	if (! newval || isDocumentShared.value) {return;}
	generatedPromptForCoverLetter.value = data.value.generated;
});

// TODO: REVERT - Remove this block when builder-entry nudge experiment is reverted
const servicesNudgesStore = useServicesNudges();
let nudgeTimeout = null;
onMounted(() => {
	// Skip the "book a call" nudge on the post-onboarding first run so it doesn't stack on
	// top of the ResumAI tutorial (?firstRun=1 is set by the onboarding hand-off).
	if (!coverLetter && !isDocumentShared.value && !query.firstRun) {
		nudgeTimeout = servicesNudgesStore.showNudgeOnceFor(servicesNudgesStore.FEATURES.resumeAi);
	}
});

onUnmounted(() => {
	clearTimeout(nudgeTimeout);
});
</script>

<style>
.builder-content {
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
}

.builder-content svg {
  outline: none !important;
}
</style>
